import { MediaMatcher } from '@angular/cdk/layout';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { JoyrideDirective, JoyrideService } from 'ngx-joyride';
import { Subject } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import { AuthorizeService } from 'src/app/_core/access-control/authorize.service';
import { FilterService } from 'src/app/_shared/service/filter.service';
import { WalkthroughService } from 'src/app/_shared/service/walkthrough.service';
import { FiltersState } from 'src/app/_shared/state/filter.state';
import { UserState } from 'src/app/_shared/state/user.state';
import { AppState } from 'src/app/app.state';
import MESSAGE from 'src/assets/resource/strings.json';
import constants from 'src/constant.json';
import { WorkspaceState } from '../../../_shared/state/workspace.state';
import { PublicRecordService } from '../../service/public-record.service';
import { PropertyState } from '../../state/property.state';

@Component({
  selector: 'app-workspace',
  templateUrl: './workspace.component.html',
  styleUrls: ['./workspace.component.scss'],
})
export class WorkspaceComponent implements OnInit, AfterViewInit {
  @ViewChildren(JoyrideDirective) joyrideSteps!: QueryList<JoyrideDirective>;
  isHamburger: boolean = false;
  isCompanyNavActive: boolean;
  stringRes: any = MESSAGE;
  mobileQuery: MediaQueryList;
  private mobileQueryListener: () => void;
  tourTimeout: any;

  // Subscription to unsubscribe on component destroy
  onDestroyNotifier$ = new Subject();

  constructor(
    media: MediaMatcher,
    private filterService: FilterService,
    private filtersState: FiltersState,
    private router: Router,
    public appState: AppState,
    public authorizeService: AuthorizeService,
    public workspaceState: WorkspaceState,
    private cdr: ChangeDetectorRef,
    private userState: UserState,
    private publicRecordService: PublicRecordService,
    private walkthroughService: WalkthroughService,
    private joyrideService: JoyrideService,
    private propertyState: PropertyState,
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this.mobileQuery.addListener(this.mobileQueryListener);
  }

  ngOnInit() {
    this.publicRecordService.initApiTrigger();

    this.userState.signupSuccessClosed
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((status) => {
        if (status) {
          this.checkPropertysheet(status);
        }
      });

    this.userState.userDetail$
      .pipe(
        filter((res) => !!res),
        first(),
        takeUntil(this.onDestroyNotifier$)
      )
      .subscribe((res) => {
        const walkthrough = res.user_profile?.walkthrough;
        if (walkthrough && Object.keys(walkthrough).length) {
          this.checkPropertysheet(true);
         // this.checkAndStartDashboardTour();
        }
      });

    this.workspaceState.workspaceViewValue = this.appState.workspaceView;
    this.workspaceState.workspaceView$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((view) => {
      this.router.navigate([`workspace/${view}`], {
        queryParamsHandling: 'preserve',
      });
    });
    this.workspaceState.activeWorkspaceMode$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      if (!res) return;
      this.appState.setAppLoadingState('switchWorkspaceMode', true);
      setTimeout(() => {
        this.appState.setAppLoadingState('switchWorkspaceMode', false);
      }, 1000);
      if (!this.filtersState.detailedFilterListValue)
        this.filterService.setDetailedFilterForBaseFilter(res.defaultFilters || {});
      this.filterService.dataLayerFilterValue('clearAllFilter', {});
    });
  }


  private checkPropertysheet(status:Boolean): void {
     const propertyDetailsInput =
      this.propertyState.propertyDetailsSheetInput;

    const isPropertyDetailsOpen =
      !!propertyDetailsInput &&
      Object.keys(propertyDetailsInput).length > 0;
    //      const url = this.router.url.split('?')[0];
    // const isPropertyDetailsUrl =
    //    /^\/workspace\/map\/(?:map\/)?[^/]+\/[^/]+\/[^/]+/.test(url);
        if (
      status &&
      !isPropertyDetailsOpen &&
      this.router.url.startsWith('/workspace') 
    ) {  
       this.checkAndStartDashboardTour();
        }
  }

  private checkAndStartDashboardTour(): void {
    this.walkthroughService
      .isWalkthroughDone(constants.WALKTHROUGH_KEYS.EXPLORE)
      .pipe(first())
      .subscribe((isDone) => {
        if (!isDone) {
          setTimeout(() => {
            this.startTour();
          }, 500);
        }
      });
  }

  startTour() {
    const steps = ['presetFilter', 'allFilters', 'savedSearch'];
    this.joyrideService
      .startTour({
        steps: steps,
        showPrevButton: true,
        stepDefaultPosition: 'bottom',
        waitingTime: 1000,
      })
      .subscribe({
        next: (event) => {
          if (event.actionType === 'NEXT' && event.number === steps.length) {
            this.walkthroughService.handleWalkthrough(constants.WALKTHROUGH_KEYS.EXPLORE);
          }
        },
        complete: () => {
          this.walkthroughService.handleWalkthrough(constants.WALKTHROUGH_KEYS.EXPLORE);
        },
      });
  }

  ngAfterViewInit() {
    const currentView = this.mobileQuery.matches ? 'mobile' : 'desktop';
    this.userState.userDetail$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      const userSession = res ? 'identified' : 'guest';
      this.setCompanyNavBar(this.appState.companyInfoValue, userSession, currentView);
    });
    this.appState.companyInfo$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res && Object.keys(res).length) {
        const userSession = this.appState.authTokenValue?.idToken ? 'identified' : 'guest';
        this.setCompanyNavBar(res, userSession, currentView);
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
    clearTimeout(this.tourTimeout);
  }

  setCompanyNavBar(companyInfo, userSession, currentView) {
    if (
      companyInfo?.alias &&
      companyInfo?.customizationConfig?.companyNavBar[0]?.activeView.includes(currentView) &&
      companyInfo?.customizationConfig?.companyNavBar[0]?.sessionStatus.includes(userSession)
    ) {
      this.isCompanyNavActive = true;
      if (!companyInfo?.customizationConfig?.companyNavBar[0]?.config?.showMenu) {
        this.isHamburger = false;
      } else {
        this.isHamburger = true;
      }
    } else {
      this.isCompanyNavActive = false;
      this.isHamburger = true;
    }
    this.cdr.detectChanges();
  }

  workspaceModeChange(event: MatSlideToggleChange) {
    this.workspaceState.activeWorkspaceModeValue = this.workspaceState.workspaceModeValue.filter((mode) => {
      if (event.checked) return mode.modeName.toLowerCase() == 'pro';
      else return mode.modeName.toLowerCase() == 'default';
    })[0];
  }
}
